import {
    Drawer, Button, Box, Divider, Link
} from '@mui/material';
import { FocusTrap } from '@mui/base/FocusTrap';
import { List as IList } from 'immutable';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from 'react-intl';

import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ListItem from '@mui/material/ListItem';
import CloseIcon from '@mui/icons-material/Close';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import LaunchSharpIcon from '@mui/icons-material/LaunchSharp';
import orderStatusMessages from '../order/messages/orderStatusMessages';
import { getIntlLocale } from '../intl/intlSelectors';
import { getCustomCashstarLinks } from '../footer/FooterSelector';
import DrawerFooterLinksDefault from './DrawerFooterLinksDefault';
import { getCurrencyPickerStatus } from '../localeCurrencyPicker/LocaleCurrencyPickerSelector';
import windowSize from '../utils/windowSize';
import dynamicLinkMessages from '../brand/messages/DynamicLinkMessages';
import headerMessages from './message/HeaderMessage';
import LanguageCurrencySelect from './LanguageCurrencySelect';
import { trackHeaderFooterClick } from '../segment/segmentModule';


const Flag = ({ countryCode, styleNames = {} }) => <i className={classnames(styleNames, 'flag', countryCode)} />;


const DrawerComponent = ({
    customLinks, intl, locale, handleClickOpen, fullySupportedLocalesUniqueArr,
    localesForSelectedCountry, isCurrencyPickerStatus, selectedCurrency, trackExternalLinkClickAction, isNewDrawer
}) => {
    const [openDrawer, setOpenDrawer] = useState(false);

    const excludedFooter = [intl.formatMessage(orderStatusMessages.faqsLabel).toLowerCase().trim(),
        intl.formatMessage(orderStatusMessages.customerSupportLabel).toLowerCase().trim()];

    const linkList = customLinks.map((link) => {
        const { text, url } = link.toJS();
        if (excludedFooter.includes(text.toLowerCase().trim()) && !isNewDrawer) {
            return null;
        }
        return (
            <ListItem>
                <Button
                    className='drawerHeaderButton'
                    href={url}
                    rel='noreferrer noopener'
                    target='_blank'
                >
                    {text}
                    { isNewDrawer && (
                        <LaunchSharpIcon
                            className="manage-gift-card-text-external-link manage-gift-card-text"
                            aria-labelledby={text}
                        />
                    )}
                </Button>
            </ListItem>
        );
    });

    const toggleDrawer = open => (event) => {
        if (
            event.type === 'keydown'
            && (event.key === 'Tab' || event.key === 'Shift')
        ) {
            return;
        }
        setOpenDrawer(open);
    };

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setOpenDrawer(false);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    return (
        <div className='headerDrawerWrapper'>
            <FocusTrap open={openDrawer} disableAutoFocus={false} disableEnforceFocus={false}>
                <Drawer
                    anchor='right'
                    variant='persistent'
                    open={openDrawer}
                    onClose={() => setOpenDrawer(false)}
                    PaperProps={{ role: 'dialog', 'aria-label': 'Menu', 'aria-modal': 'true' }}
                >
                    <Box>
                        <IconButton
                            aria-label="Close menu"
                            className='header-Drawer-Close-Icon'
                            onClick={() => { setOpenDrawer(false); }}
                        >
                            <CloseIcon />
                        </IconButton>
                        <div className='drawer-content'>
                            <div className='manage-gift-cards header-text'>
                                <Link
                                    className='manage-gift-card-text'
                                    href={intl.formatMessage(
                                        dynamicLinkMessages.manageGiftCard
                                    )}
                                    disableRipple
                                    rel='noreferrer noopener'
                                    target='_blank'
                                    onClick={() => {
                                        trackExternalLinkClickAction('Header Link Click',
                                            {
                                                title: intl.formatMessage(headerMessages.manageGiftCard),
                                                path: intl.formatMessage(dynamicLinkMessages.manageGiftCard)
                                            });
                                    }}
                                >
                                    <CardGiftcardIcon className='manage-gift-card-icon' />
                                    <p>
                                        {intl.formatMessage(
                                            headerMessages.manageGiftCard
                                        )}
                                        <LaunchSharpIcon
                                            className="manage-gift-card-text-external-link manage-gift-card-text"
                                            aria-labelledby={`${intl.formatMessage(headerMessages.manageGiftCard)}`}
                                        />
                                    </p>
                                </Link>
                            </div>
                            <LanguageCurrencySelect
                                intl={intl}
                                locale={locale}
                                fullySupportedLocalesUniqueArr={
                                    fullySupportedLocalesUniqueArr
                                }
                                localesForSelectedCountry={
                                    localesForSelectedCountry
                                }
                                isCurrencyPickerStatus={
                                    isCurrencyPickerStatus
                                }
                                handleClickOpen={handleClickOpen}
                                selectedCurrency={selectedCurrency}
                            />
                        </div>
                        <Divider className='drawerDivider' />
                        <Box>
                            {customLinks.size ? linkList : <DrawerFooterLinksDefault />}
                        </Box>
                    </Box>
                </Drawer>
            </FocusTrap>
            <IconButton
                edge="start"
                aria-label="Open menu"
                aria-haspopup="dialog"
                aria-expanded={openDrawer}
                onClick={toggleDrawer(true)}
                className='headerDrawerIcon'
            >
                <MenuIcon />
            </IconButton>
        </div>
    );
};

Flag.defaultProps = {
    styleNames: {}
};

Flag.propTypes = {
    countryCode: PropTypes.string.isRequired,
    styleNames: PropTypes.object
};

DrawerComponent.defaultProps = {
    handleClickOpen: null,
    isNewDrawer: false
};

DrawerComponent.propTypes = {
    customLinks: PropTypes.instanceOf(IList).isRequired,
    intl: intlShape.isRequired,
    locale: PropTypes.string.isRequired,
    handleClickOpen: PropTypes.func,
    fullySupportedLocalesUniqueArr: PropTypes.instanceOf(IList).isRequired,
    localesForSelectedCountry: PropTypes.object.isRequired,
    isCurrencyPickerStatus: PropTypes.bool.isRequired,
    isNewDrawer: PropTypes.bool,
    selectedCurrency: PropTypes.string.isRequired,
    trackExternalLinkClickAction: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
    customLinks: getCustomCashstarLinks(state),
    locale: getIntlLocale(state),
    isCurrencyPickerStatus: getCurrencyPickerStatus(state)
});

const mapDispatchToProps = {
    trackExternalLinkClickAction: trackHeaderFooterClick
};

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(windowSize(DrawerComponent)));
